/**
 * Context-menu action generation + execution for `pinned-workspace` tree
 * rows (Task 16). Mirrors the per-object-type `generate*ContextMenuActions` /
 * `execute*ContextMenuAction` pairs used by every other node type (e.g.
 * `tableContextMenuService.ts`) — `pinned-workspace` has no canonical
 * underlying node to unwrap to (unlike `pinned-object` et al., which reuse
 * the canonical table/view/... generators via `canonicalNodeTypeForPin`), so
 * it gets its own dedicated pair here.
 *
 * Produces exactly the 4 actions from the spec: `Open`, `Rename…`,
 * `Update from open tab` (enabled iff a linked tab has diverged from the
 * pin's stored definition — see `pinDivergence.ts`), `Unpin`.
 */

import { toast } from '../../../../components/ui/toaster';
import type { ContextMenuAction } from '../../../explorer/models/TreeModel';
import type { PinnedWorkspaceRef } from '../../../../types/electron';
import type { TrackFunction } from '../../../../core/analytics/analyticsTypes';
import type { Tab } from '../../../workspace/core/types/workspaceTypes';
import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import { capturePinDefinition } from '../../../workspace/common/services/pinCaptureRegistry';
import {
  isFileSourcePin,
  workspacePinTelemetry,
  type WorkspacePinTelemetry,
} from '../../../workspace/common/services/workspacePinCreate';
import { pinScopeForTab } from '../../../workspace/common/services/comparePin';
import { isPinDiverged } from './pinDivergence';
import { findLinkedTabs, mostRecentlyActive, openWorkspacePin } from './openWorkspacePin';
import { usePinnedObjectsStore } from '../../store/pinnedObjectsStore';

/**
 * Tabs linked to `ref` (via `sourcePin`) whose live definition has diverged.
 *
 * File-source pins (G1 in `pinDivergence.ts`) never diverge on content — only
 * a database rebind (F2) counts — and `isPinDiverged`'s file-source branches
 * never read `captured`, so the database check runs directly here without
 * requiring a live `capturePinDefinition` callback (none is registered for
 * file-backed tabs today).
 */
function divergedLinkedTabs(connectionId: string, ref: PinnedWorkspaceRef): Tab[] {
  const ws = useWorkspaceStore.getState();
  const linked = findLinkedTabs(connectionId, ref.id, ws);
  const isFileSource = isFileSourcePin(ref);
  return linked.filter((tab) => {
    // B5 — `pinScopeForTab`, not `connectionInfo` directly: a comparison's
    // envelope database is its SOURCE database, while its `connectionInfo`
    // names the target. Reading `connectionInfo` here made every pinned
    // comparison read as permanently diverged via `isPinDiverged`'s F2
    // database check.
    const scope = pinScopeForTab(tab);
    // B5 — a comparison whose SOURCE was re-pointed at a DIFFERENT connection
    // is out of this pin's reach. The pin lives in the old connection's file;
    // rewriting its payload there would file a comparison under a connection
    // it no longer belongs to (the on-disk validator can't catch that — it
    // never sees which file it's being written into). Other pin kinds don't
    // need this guard: `workspaceStore` severs their `sourcePin` on a
    // connection change, so they never reach here still linked.
    if (scope.connectionId !== undefined && scope.connectionId !== connectionId) return false;
    const tabDatabase = scope.database;
    if (isFileSource) {
      return tabDatabase !== ref.database;
    }
    const captured = capturePinDefinition(tab.id);
    if (!captured) return false;
    return isPinDiverged(ref, captured, tabDatabase);
  });
}

export type UpdateFromTabAvailability = {
  hasLinkedTab: boolean;
  hasDivergedTab: boolean;
};

/** Pure-enough (single `.getState()` snapshot read) availability check for the menu. */
export function getUpdateFromTabAvailability(
  connectionId: string,
  ref: PinnedWorkspaceRef,
): UpdateFromTabAvailability {
  const ws = useWorkspaceStore.getState();
  const linked = findLinkedTabs(connectionId, ref.id, ws);
  return {
    hasLinkedTab: linked.length > 0,
    hasDivergedTab: divergedLinkedTabs(connectionId, ref).length > 0,
  };
}

export function generatePinnedWorkspaceContextMenuActions(
  availability: UpdateFromTabAvailability,
): ContextMenuAction[] {
  const updateDisabled = !availability.hasDivergedTab;
  const disabledReason = !availability.hasLinkedTab
    ? 'Open this pin in a tab first'
    : 'The open tab matches this pin — nothing to update';

  return [
    { id: 'open', label: 'Open' },
    { id: 'rename', label: 'Rename…', icon: 'edit' },
    {
      id: 'update-from-tab',
      label: 'Update from open tab',
      icon: 'refresh',
      disabled: updateDisabled,
      disabledReason: updateDisabled ? disabledReason : undefined,
    },
    { id: 'unpin-separator', label: '', isSeparator: true },
    { id: 'unpin', label: 'Unpin', icon: 'pin-off' },
  ];
}

/**
 * `{ workspaceType, source }` analytics fields shared by open/rename/update/
 * unpin. Delegates to `workspacePinTelemetry` so the tree-row actions and the
 * tab-strip/toolbar actions can never drift (F4).
 */
export function workspacePinAnalyticsFields(ref: PinnedWorkspaceRef): WorkspacePinTelemetry {
  return workspacePinTelemetry(ref);
}

/**
 * Human toast `description` for a rejected pin mutation, or `undefined` when
 * the toast title alone is honest enough. The size-cap rejections from
 * `connectionMetaInfo.ts` carry actionable, user-safe messages (e.g. "pin is
 * too large to save (payload exceeds 256 KB)") tagged with a stable token —
 * surface those; everything else (unknown pin id from a concurrent remote
 * unpin, I/O failures) gets the generic title only, since raw internals like
 * "unknown pin id" read as gibberish in a toast.
 */
function describePinMutationError(err: unknown): string | undefined {
  const message = err instanceof Error ? err.message : String(err);
  if (message.includes('PIN_PAYLOAD_TOO_LARGE') || message.includes('PIN_DOC_TOO_LARGE')) {
    return message
      .replace(/^.*connectionMetaInfo:\s*/, '') // module (and any IPC-wrapper) prefix
      .replace(/\s*\((PIN_PAYLOAD_TOO_LARGE|PIN_DOC_TOO_LARGE)\)\s*$/, ''); // token suffix
  }
  return undefined;
}

/**
 * Rename flow shared with `DatabaseExplorer`'s `RenamePinDialog`: track fires
 * only AFTER the store write succeeds (spec §7), and `onSuccess` (the caller
 * closes the dialog there) is only invoked on success — on rejection the
 * dialog stays open with the user's input intact and a toast explains why.
 */
export async function renameWorkspacePin(args: {
  connectionId: string;
  ref: PinnedWorkspaceRef;
  name: string;
  track: TrackFunction;
  onSuccess?: () => void;
}): Promise<void> {
  const { connectionId, ref, name, track, onSuccess } = args;
  try {
    await usePinnedObjectsStore.getState().updatePin(connectionId, ref.id, { name });
  } catch (err) {
    toast.error({ title: 'Could not rename pin', description: describePinMutationError(err) });
    return;
  }
  track('feature:workspace_pin_rename', workspacePinAnalyticsFields(ref));
  onSuccess?.();
}

/**
 * `Update from open tab` — picks the most-recently-active DIVERGED linked
 * tab, captures its live definition, and writes it back onto the pin. File
 * pins (source === 'file') only patch `database` — content drift against a
 * file-backed pin is expected and never round-tripped (G1 in `pinDivergence.ts`).
 */
async function updatePinFromLinkedTab(
  connectionId: string,
  ref: PinnedWorkspaceRef,
  track: TrackFunction,
): Promise<void> {
  const ws = useWorkspaceStore.getState();
  const diverged = divergedLinkedTabs(connectionId, ref);
  if (diverged.length === 0) return;
  const tab = mostRecentlyActive(diverged, ws);
  // B5 — same source-derived scope as `divergedLinkedTabs` above. Patching the
  // envelope with a compare tab's `connectionInfo.database` (the TARGET's)
  // would be rejected on disk by the envelope/payload equality rule.
  const tabDatabase = pinScopeForTab(tab).database ?? ref.database;
  const isFileSource = isFileSourcePin(ref);

  if (isFileSource) {
    await usePinnedObjectsStore.getState().updatePin(connectionId, ref.id, { database: tabDatabase });
  } else {
    const captured = capturePinDefinition(tab.id);
    if (!captured) return;
    await usePinnedObjectsStore.getState().updatePin(connectionId, ref.id, {
      database: tabDatabase,
      payload: captured.payload,
    });
  }
  track('feature:workspace_pin_update', workspacePinAnalyticsFields(ref));
}

export function executePinnedWorkspaceContextMenuAction(
  actionId: string,
  args: { connectionId: string; ref: PinnedWorkspaceRef },
  handlers: { onRename: () => void; track: TrackFunction },
): void {
  const { connectionId, ref } = args;
  switch (actionId) {
    case 'open':
      void openWorkspacePin({ connectionId, ref });
      return;
    case 'rename':
      handlers.onRename();
      return;
    case 'update-from-tab':
      // Rejections (size caps, unknown pin id from a concurrent remote unpin,
      // I/O) surface as a toast — never an unhandled rejection. The success
      // track fires inside `updatePinFromLinkedTab` AFTER the store write.
      updatePinFromLinkedTab(connectionId, ref, handlers.track).catch((err: unknown) => {
        toast.error({ title: 'Could not update pin', description: describePinMutationError(err) });
      });
      return;
    case 'unpin':
      // Spec §7 — analytics fire after SUCCESS, not on click; a rejected
      // unpin (over-cap doc, I/O failure) gets a toast and no event.
      usePinnedObjectsStore
        .getState()
        .unpin(connectionId, ref)
        .then(() => {
          handlers.track('feature:workspace_pin_unpin', workspacePinAnalyticsFields(ref));
        })
        .catch((err: unknown) => {
          toast.error({ title: 'Could not unpin', description: describePinMutationError(err) });
        });
      return;
    default:
      return;
  }
}
